import type { FieldPath } from 'firebase/firestore';
import type { ObjectDotNotation } from '@sda/helpers/helpers.types';
import type { QueryObject, QueryValue, SetDataType } from '../interfaces';

type ObjectDotNotationPath<T> = ObjectDotNotation<T> | FieldPath;

export class QueryHelper<T extends SetDataType> {
  /* Helper methods for making where queries easier to chain */

  /**
   * Checks if the specified attribute matches the value:
   *
   * @param  {string} attribute
   * @param  {QueryValue} value
   * @return {QueryObject}
   */
  isEqualTo = (attribute: ObjectDotNotationPath<T>, value: QueryValue): QueryObject<T> => ({
    attribute,
    value,
    operator: '==',
  });

  /**
   * Checks if the specified attribute does not match the value
   *
   * @param  {string} attribute
   * @param  {QueryValue} value
   * @return {QueryObject}
   */
  isNotEqualTo = (attribute: ObjectDotNotationPath<T>, value: QueryValue): QueryObject<T> => ({
    attribute,
    value,
    operator: '!=',
  });

  /**
   * Checks if the specified attribute contains one of the specified values
   *
   * @param  {string} attribute
   * @param  {any[]} value
   * @return {QueryObject}
   */
  isIn = (attribute: ObjectDotNotationPath<T>, value: string[]): QueryObject<T> => ({
    attribute,
    value,
    operator: 'in',
  });

  /**
   * Checks if the specified attribute does not contain one of the specified values
   *
   * @param  {string} attribute
   * @param  {any[]} value
   * @return {QueryObject}
   */
  isNotIn = (attribute: ObjectDotNotationPath<T>, value: string[]): QueryObject<T> => ({
    attribute,
    value,
    operator: 'not-in',
  });

  /**
   * Checks if the specified attribute is greather than the value
   *
   * @param  {string} attribute
   * @param  {QueryValue} value
   * @return {QueryObject}
   */
  isGreaterThan = (attribute: ObjectDotNotationPath<T>, value: QueryValue): QueryObject<T> => ({
    attribute,
    value,
    operator: '>',
  });

  /**
   * Checks if the specified attribute is greather than or equal to the value
   *
   * @param  {string} attribute
   * @param  {QueryValue} value
   * @return {QueryObject}
   */
  isGreaterThanOrEqualTo = (attribute: ObjectDotNotationPath<T>, value: QueryValue): QueryObject<T> => ({
    attribute,
    value,
    operator: '>=',
  });

  /**
   * Checks if the specified attribute is less than the value
   *
   * @param  {string} attribute
   * @param  {QueryValue} value
   * @return {QueryObject}
   */
  isLessThan = (attribute: ObjectDotNotationPath<T>, value: QueryValue): QueryObject<T> => ({
    attribute,
    value,
    operator: '<',
  });

  /**
   * Checks if the specified attribute is less than or equal to the value
   *
   * @param  {string} attribute
   * @param  {QueryValue} value
   * @return {QueryObject}
   */
  isLessThanOrEqualTo = (attribute: ObjectDotNotationPath<T>, value: QueryValue): QueryObject<T> => ({
    attribute,
    value,
    operator: '<=',
  });

  /**
   * Checks if the specified attribute is boolean true
   *
   * @param  {string} attribute
   * @return {QueryObject}
   */
  isTrue = (attribute: ObjectDotNotationPath<T>): QueryObject<T> => ({
    attribute,
    value: true,
    operator: '==',
  });

  /**
   * Checks if the specified attribute is boolean false
   *
   * @param  {string} attribute
   * @return {QueryObject}
   */
  isFalse = (attribute: ObjectDotNotationPath<T>): QueryObject<T> => ({
    attribute,
    value: false,
    operator: '==',
  });

  /**
   * Checks if the specified attribute is of the null type
   *
   * @param  {string} attribute
   * @return {QueryObject}
   */
  isNull = (attribute: ObjectDotNotationPath<T>): QueryObject<T> => ({
    attribute,
    value: null,
    operator: '==',
  });

  /**
   * Checks if the specified attribute is not of the null type
   *
   * @param  {string} attribute
   * @return {QueryObject}
   */
  isNotNull = (attribute: ObjectDotNotationPath<T>): QueryObject<T> => ({
    attribute,
    value: null,
    operator: '!=',
  });

  /**
   * Checks if the specified attribute has any string value
   * Note: This counts as a range for query limits
   *
   * @param  {string} attribute
   * @return {QueryObject}
   */
  hasString = (attribute: ObjectDotNotationPath<T>): QueryObject<T> => ({
    attribute,
    value: '',
    operator: '>',
  });

  /**
   * Checks to see if array specified by attribute contains the value
   * @param {string} attribute
   * @param {QueryValue} value
   * @return {QueryObject}
   */
  contains = (attribute: ObjectDotNotationPath<T>, value: QueryValue): QueryObject<T> => ({
    attribute,
    value,
    operator: 'array-contains',
  });

  /**
   * Checks if the array specified by attribute contains one of the specified values
   *
   * @param  {string} attribute
   * @param  {any[]} value
   * @return {QueryObject}
   */
  containsAny = (attribute: ObjectDotNotationPath<T>, value: string[]): QueryObject<T> => ({
    attribute,
    value,
    operator: 'array-contains-any',
  });
}
